import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Collapse, List, Select, Stack, Text, TextInput } from '@mantine/core';
import { DateOfBirthInput } from '@/components/ui/DateOfBirthInput';
import { StatusPill } from '@/components/ui/StatusPill';
import { Callout as UiCallout } from '@/components/ui/Callout';
import { IconCheck, IconAlertTriangle, IconX } from '@tabler/icons-react';
import {
  useRidesMeService,
  validateSouthAfricanId,
  validatePassportNumber,
  RIDES_DOCUMENT_TYPE,
  RIDES_SEX,
  RIDES_RULE_CODES,
  RIDES_VERIFICATION_STATUS,
  type RidesDocumentCheckRequest,
  type RidesDocumentCheckResult,
} from '@actschurch/ccms-sdk';
import { useRidesLookups, countryOptions } from '@/lib/lookups';
import { useCopy } from '@/lib/settings';
import { checkItemCopy, dobCorrectionNotice, ruleCodeCopy, verificationStatusCopy } from '@/lib/copy';
import { formatDate, parseApiDate, todayYmd, toYmd } from '@/lib/format';
import { ExpiryDateInput } from '@/components/ui/ExpiryDateInput';
import { ErrorAlert } from './ErrorAlert';

interface Props {
  /** RIDES_VERIFICATION_TYPE.GovernmentId (1) or GuardianId (3) */
  typeId: number;
  /** The sex the member declared (RIDES_SEX); the SA ID sex digit is compared to it live. */
  expectedSexId?: number | null;
  /** yyyy-MM-dd from the member profile, compared to the SA ID date live. */
  expectedDob?: string | null;
  /** Called with the server result after a successful POST (Pending / Verified / Failed). */
  onResult: (result: RidesDocumentCheckResult) => void;
  /** Runs just before the POST (e.g. upsert the rider profile). Throwing aborts the submission. */
  beforeSubmit?: (payload: { sexId: number | null; dateOfBirth: string | null }) => Promise<void>;
  submitLabel?: string;
  disabled?: boolean;
  whose?: 'your' | "the guardian's";
}

type DocKind = 'said' | 'passport';

/**
 * SA ID / passport entry with live, plain-language checks from the SDK rules. The number
 * lives only in component state and the request body, never in the wizard store or storage.
 */
export function IdentityDocumentForm({ typeId, expectedSexId, expectedDob, onResult, beforeSubmit, submitLabel, disabled, whose = 'your' }: Props) {
  const me = useRidesMeService();
  const { data: lookups } = useRidesLookups();
  const { t, settings } = useCopy();
  const allowPassport = settings.allowPassport;
  const [kind, setKind] = useState<DocKind>('said');
  const effectiveKind: DocKind = allowPassport ? kind : 'said';

  // SA ID
  const [idNumber, setIdNumber] = useState('');
  // Passport
  const [country, setCountry] = useState<string | null>('ZAF');
  const [passport, setPassport] = useState('');
  const [expiry, setExpiry] = useState<Date | null>(null);
  const [dob, setDob] = useState<string | null>(null);
  const [showMrz, setShowMrz] = useState(false);
  const [sexId, setSexId] = useState<number | null>(expectedSexId ?? null);
  const [mrz, setMrz] = useState('');

  const idCheck = useMemo(() => (idNumber.replace(/\s+/g, '').length ? validateSouthAfricanId(idNumber) : null), [idNumber]);
  const idProblems = useMemo(() => {
    if (!idCheck) return [] as string[];
    const list = idCheck.codes.map((c) => ruleCodeCopy(c, { document: 'id' }));
    if (idCheck.valid && expectedSexId && idCheck.sexId && idCheck.sexId !== expectedSexId) {
      list.push(ruleCodeCopy(RIDES_RULE_CODES.SexMismatch, { declaredSexId: expectedSexId, documentSexId: idCheck.sexId }));
    }
    // A date of birth that differs from the profile no longer blocks a valid SA ID: the ID wins and the
    // server corrects the profile (dobNotice below tells the person before they send it).
    return list;
  }, [idCheck, expectedSexId]);

  const dobNotice = useMemo(() => {
    if (!idCheck?.valid || !expectedDob || !idCheck.dateOfBirth) return null;
    if (idCheck.dateOfBirth === expectedDob.slice(0, 10)) return null;
    const day = (ymd: string) => formatDate(ymd).replace(/^\w+, /, '');
    return dobCorrectionNotice(day(expectedDob.slice(0, 10)), day(idCheck.dateOfBirth), whose);
  }, [idCheck, expectedDob, whose]);

  const passportCheck = useMemo(
    () => (passport.trim().length ? validatePassportNumber(passport, country, expiry ? toYmd(expiry) : null, mrz || null) : null),
    [passport, country, expiry, mrz],
  );

  const saidReady = !!idCheck && idCheck.valid && idProblems.length === 0;
  const passportReady = !!passportCheck && passportCheck.valid && !!dob && !!sexId;
  const ready = effectiveKind === 'said' ? saidReady : passportReady;

  const submit = useMutation({
    mutationFn: async () => {
      const body: RidesDocumentCheckRequest =
        effectiveKind === 'said'
          ? {
              documentTypeID: RIDES_DOCUMENT_TYPE.SouthAfricanId,
              documentNumber: idNumber.replace(/\s+/g, ''),
              issuingCountry: 'ZAF',
              dateOfBirth: idCheck?.dateOfBirth ?? null,
              sexID: idCheck?.sexId ?? expectedSexId ?? null,
            }
          : {
              documentTypeID: RIDES_DOCUMENT_TYPE.Passport,
              documentNumber: passportCheck?.normalizedNumber ?? passport.trim().toUpperCase(),
              issuingCountry: country,
              expiresAt: expiry ? toYmd(expiry) : null,
              dateOfBirth: dob,
              sexID: sexId,
              mrzLine2: mrz.trim() || null,
            };
      if (beforeSubmit) await beforeSubmit({ sexId: body.sexID ?? null, dateOfBirth: body.dateOfBirth ?? null });
      const result = await me.startVerification(typeId, body);
      // Never keep the number around once it has been sent.
      setIdNumber('');
      setPassport('');
      setMrz('');
      return result;
    },
    onSuccess: (r) => onResult(r),
  });

  const result = submit.data;
  const ctx = { declaredSexId: expectedSexId, document: effectiveKind === 'said' ? ('id' as const) : ('passport' as const) };
  const resultPill =
    result?.statusID === RIDES_VERIFICATION_STATUS.Verified
      ? { tone: 'ok' as const, label: 'Checked' }
      : result?.statusID === RIDES_VERIFICATION_STATUS.Pending
        ? { tone: 'warn' as const, label: 'Waiting for a check' }
        : { tone: verificationStatusCopy(result?.statusID).kind, label: verificationStatusCopy(result?.statusID).label };
  const owner = whose === 'your' ? 'Your' : "The parent or guardian's";

  return (
    <Stack gap="md">
      {allowPassport && (
        <div className="rides-seg" role="radiogroup" aria-label="Document type">
          <button type="button" role="radio" className="rides-segbtn" aria-checked={effectiveKind === 'said'} onClick={() => setKind('said')} disabled={disabled}>
            SA ID<small>Green book or smart card</small>
          </button>
          <button type="button" role="radio" className="rides-segbtn" aria-checked={effectiveKind === 'passport'} onClick={() => setKind('passport')} disabled={disabled}>
            Passport<small>From any country</small>
          </button>
        </div>
      )}

      {effectiveKind === 'said' ? (
        <Stack gap="xs">
          <TextInput
            label={`${owner} SA ID number`}
            placeholder="13 digits"
            inputMode="numeric"
            autoComplete="off"
            maxLength={15}
            value={idNumber}
            onChange={(e) => setIdNumber(e.currentTarget.value.replace(/[^\d\s]/g, ''))}
            disabled={disabled}
            error={idProblems.length ? idProblems[0] : undefined}
            rightSection={saidReady ? <IconCheck size={18} color="var(--rides-ok)" aria-label="Looks right" /> : undefined}
            description="We check the number. We don't keep it."
          />
          {saidReady && idCheck && (
            <Text size="sm" c="dimmed" role="status">
              This reads as: born {formatDate(idCheck.dateOfBirth).replace(/^\w+, /, '')}, {idCheck.sexId === RIDES_SEX.Female ? 'female' : 'male'}
              {idCheck.citizenship === 'citizen' ? ', SA citizen' : idCheck.citizenship === 'permanent_resident' ? ', permanent resident' : ''}.
            </Text>
          )}
          {saidReady && dobNotice && <UiCallout tone="info">{dobNotice}</UiCallout>}
          {idProblems.length > 1 && (
            <List size="sm" c="var(--rides-bad)" spacing={2}>
              {idProblems.slice(1).map((p) => <List.Item key={p}>{p}</List.Item>)}
            </List>
          )}
        </Stack>
      ) : (
        <Stack gap="md">
          <Select
            label="Country that issued it"
            data={countryOptions(lookups)}
            value={country}
            onChange={setCountry}
            searchable
            nothingFoundMessage="No country found"
            disabled={disabled}
            placeholder="Choose a country"
          />
          <TextInput
            label="Passport number"
            placeholder="As printed on the photo page"
            autoComplete="off"
            value={passport}
            onChange={(e) => setPassport(e.currentTarget.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            disabled={disabled}
            maxLength={9}
            description="We check the number. We don't keep it."
            error={passportCheck && !passportCheck.valid && passportCheck.codes.includes(RIDES_RULE_CODES.Format) ? ruleCodeCopy(RIDES_RULE_CODES.Format, ctx) : undefined}
            rightSection={passportCheck?.valid ? <IconCheck size={18} color="var(--rides-ok)" aria-label="Looks right" /> : undefined}
          />
          {passportCheck?.warnings.includes(RIDES_RULE_CODES.PassportPatternWarning) && (
            <Text size="sm" c="var(--rides-warn)">{ruleCodeCopy(RIDES_RULE_CODES.PassportPatternWarning)}</Text>
          )}
          <ExpiryDateInput
            label="Expiry date"
            value={expiry}
            onChange={(ymd) => setExpiry(ymd ? parseApiDate(ymd) : null)}
            minDate={todayYmd()}
            disabled={disabled}
            error={passportCheck?.codes.includes(RIDES_RULE_CODES.Expired) ? ruleCodeCopy(RIDES_RULE_CODES.Expired, ctx) : undefined}
          />
          <DateOfBirthInput label="Date of birth (as on the passport)" value={dob} onChange={setDob} disabled={disabled} />
          <div>
            <Text size="md" fw={600} mb={6}>Sex (as on the passport)</Text>
            <div className="rides-seg" role="radiogroup" aria-label="Sex on the passport">
              <button type="button" role="radio" className="rides-segbtn" aria-checked={sexId === RIDES_SEX.Female} onClick={() => setSexId(RIDES_SEX.Female)} disabled={disabled}>Female</button>
              <button type="button" role="radio" className="rides-segbtn" aria-checked={sexId === RIDES_SEX.Male} onClick={() => setSexId(RIDES_SEX.Male)} disabled={disabled}>Male</button>
            </div>
            {expectedSexId && sexId && sexId !== expectedSexId && (
              <Text size="sm" c="var(--rides-bad)" mt={6}>{ruleCodeCopy(RIDES_RULE_CODES.SexMismatch, { declaredSexId: expectedSexId, documentSexId: sexId })}</Text>
            )}
          </div>
          <div>
            <button type="button" className="rides-linkbtn" aria-expanded={showMrz} onClick={() => setShowMrz((v) => !v)}>
              Add the code line from the photo page (optional)
            </button>
            <Collapse in={showMrz}>
              <TextInput
                mt="xs"
                label="Code line (second line)"
                description="The line of letters, numbers and <<< signs at the bottom of the photo page. It helps us check the number."
                autoComplete="off"
                value={mrz}
                onChange={(e) => setMrz(e.currentTarget.value.toUpperCase())}
                disabled={disabled}
                error={passportCheck?.codes.includes(RIDES_RULE_CODES.MrzCheckDigit) ? ruleCodeCopy(RIDES_RULE_CODES.MrzCheckDigit) : undefined}
                styles={{ input: { fontFamily: 'ui-monospace, Menlo, monospace', letterSpacing: '0.06em' } }}
              />
            </Collapse>
          </div>
        </Stack>
      )}

      <ErrorAlert error={submit.error} />

      {result && (
        <div className="rides-card" style={{ padding: 14 }} role="status" aria-live="polite">
          <Stack gap={8}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Text fw={700}>{result.passed ? 'Your details look right' : 'Something needs fixing'}</Text>
              <StatusPill tone={resultPill.tone}>{resultPill.label}</StatusPill>
            </div>
            {result.checks.length > 0 && (
              <List size="sm" spacing={4} center>
                {result.checks.map((c) => (
                  <List.Item
                    key={c.code}
                    icon={c.passed ? <IconCheck size={16} color="var(--rides-ok)" /> : c.warning ? <IconAlertTriangle size={16} color="var(--rides-warn)" /> : <IconX size={16} color="var(--rides-bad)" />}
                  >
                    {checkItemCopy(c.code, c.passed, c.warning, ctx)}
                  </List.Item>
                ))}
              </List>
            )}
            {result.statusID === RIDES_VERIFICATION_STATUS.Pending && (
              <UiCallout tone="info">
                {result.verification?.providerName === 'Simulated'
                  ? verificationStatusCopy(result.statusID, null, result.verification?.providerName).detail
                  : t('verify.pending_message')}
              </UiCallout>
            )}
            {result.failureCode && !result.passed && <Text size="sm" c="var(--rides-bad)">{ruleCodeCopy(result.failureCode, ctx)}</Text>}
          </Stack>
        </div>
      )}

      {!result?.passed && (
        <Button fullWidth disabled={!ready || disabled} loading={submit.isPending} onClick={() => submit.mutate()}>
          {submitLabel ?? 'Check this document'}
        </Button>
      )}
      {!result && (
        <Text size="sm" c="dimmed">
          A campus admin confirms it in person. We never keep a copy of the document.
        </Text>
      )}
    </Stack>
  );
}
